import React, { useState } from 'react';
import { Icon } from '@iconify/react';
import { LoyaltyProgram, AddMemberPayload } from '@/types/loyalty';
import loyaltyService from '@/services/loyaltyService';
import showToast from '@/components/ui/Toast';
import { getApiErrorMessage } from '@/utils/apiErrorMessage';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  arenaSlug: string;
  program: LoyaltyProgram;
  onSuccess: () => void;
}

export const AddMemberModal: React.FC<Props> = ({
  isOpen,
  onClose,
  arenaSlug,
  program,
  onSuccess,
}) => {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [durationMonths, setDurationMonths] = useState<number>(
    program.membership_duration_months || 12
  );
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [paymentMethod, setPaymentMethod] = useState<'cash' | 'transfer'>('cash');
  const [submitting, setSubmitting] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !phone.trim()) {
      showToast.error('Nama dan Nomor WhatsApp pelanggan wajib diisi');
      return;
    }

    try {
      setSubmitting(true);
      const payload: AddMemberPayload = {
        customer_name: name.trim(),
        customer_phone: phone.trim(),
        duration_months: durationMonths,
        payment_method: paymentMethod,
        start_date: startDate,
      };

      await loyaltyService.addMember(arenaSlug, payload);
      showToast.success('Member berhasil didaftarkan');
      onSuccess();
      onClose();
    } catch (err) {
      showToast.error(getApiErrorMessage(err, 'Gagal menambahkan member'));
    } finally {
      setSubmitting(false);
    }
  };

  const regFee = program.membership_registration_fee || 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[rgba(14,23,38,0.5)]">
      <div className="w-full max-w-[420px] rounded-[16px] bg-white border border-[#E0E6ED] shadow-[0_4px_12px_rgba(14,23,38,.12)] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-[#E0E6ED]">
          <h2 className="text-[18px] font-bold text-[#0E1726]">Tambah Member</h2>
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-full text-[#8C8C8C] hover:bg-[#F6F6F6] hover:text-[#1F1F1F] transition-colors"
          >
            <Icon icon="ph:x" className="w-4 h-4" />
          </button>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="space-y-1.5">
            <label className="text-[13px] font-semibold text-[#0E1726]">Nama Pelanggan *</label>
            <input
              type="text"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Contoh: Budi Santoso"
              className="w-full h-11 rounded-full border border-[#E0E6ED] bg-white px-4 text-[14px] text-[#1F1F1F] placeholder:text-[#8C8C8C] focus:border-[#FC480D] focus:outline-none"
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-[13px] font-semibold text-[#0E1726]">Nomor WhatsApp *</label>
            <input
              type="tel"
              required
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="Contoh: 08123456789"
              className="w-full h-11 rounded-full border border-[#E0E6ED] bg-white px-4 text-[14px] text-[#1F1F1F] placeholder:text-[#8C8C8C] focus:border-[#FC480D] focus:outline-none"
            />
          </div>

          {/* Masa Aktif & Tanggal Mulai */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label className="text-[13px] font-semibold text-[#0E1726]">Masa Aktif</label>
              <select
                value={durationMonths}
                onChange={(e) => setDurationMonths(Number(e.target.value))}
                className="w-full h-11 rounded-full border border-[#E0E6ED] bg-white px-4 text-[14px] text-[#1F1F1F] focus:border-[#FC480D] focus:outline-none"
              >
                <option value={1}>1 Bulan</option>
                <option value={3}>3 Bulan</option>
                <option value={6}>6 Bulan</option>
                <option value={12}>12 Bulan</option>
              </select>
            </div>

            <div className="space-y-1.5">
              <label className="text-[13px] font-semibold text-[#0E1726]">Mulai</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full h-11 rounded-full border border-[#E0E6ED] bg-white px-4 text-[14px] text-[#1F1F1F] focus:border-[#FC480D] focus:outline-none"
              />
            </div>
          </div>

          {/* Metode Pembayaran */}
          <div className="space-y-2">
            <label className="text-[13px] font-semibold text-[#0E1726]">Metode Pembayaran</label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setPaymentMethod('cash')}
                className={`flex items-center justify-center gap-1.5 h-10 px-4 rounded-full border text-[13px] font-semibold transition-colors ${
                  paymentMethod === 'cash'
                    ? 'border-[#FC480D] bg-[#FFF5EE] text-[#FC480D]'
                    : 'border-[#E0E6ED] bg-white text-[#1F1F1F] hover:bg-[#F6F6F6]'
                }`}
              >
                <Icon icon="ph:money" className="w-4 h-4" />
                Tunai
              </button>
              <button
                type="button"
                onClick={() => setPaymentMethod('transfer')}
                className={`flex items-center justify-center gap-1.5 h-10 px-4 rounded-full border text-[13px] font-semibold transition-colors ${
                  paymentMethod === 'transfer'
                    ? 'border-[#FC480D] bg-[#FFF5EE] text-[#FC480D]'
                    : 'border-[#E0E6ED] bg-white text-[#1F1F1F] hover:bg-[#F6F6F6]'
                }`}
              >
                <Icon icon="ph:bank" className="w-4 h-4" />
                Transfer Bank
              </button>
            </div>
          </div>

          {/* Fee Summary */}
          <div className="rounded-[12px] bg-[#FFF5EE] border border-[#FC480D]/30 p-3.5 flex items-center justify-between">
            <span className="text-[13px] text-[#1F1F1F] font-medium">Biaya Pendaftaran:</span>
            <span className="text-[14px] font-bold text-[#FC480D]">
              Rp. {regFee.toLocaleString('id-ID')}
            </span>
          </div>

          {/* Footer Buttons per design system (Secondary Batal then Primary Aksi, 40px height) */}
          <div className="flex items-center justify-end gap-2 pt-2 border-t border-[#E0E6ED]">
            <button
              type="button"
              disabled={submitting}
              onClick={onClose}
              className="h-10 px-[18px] rounded-full bg-[#FFF5EE] hover:bg-[#ffe8db] text-[#FC480D] text-[14px] font-semibold transition-colors"
            >
              Batal
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex items-center gap-1.5 h-10 px-[18px] rounded-full bg-[#FC480D] hover:bg-[#E8490F] text-white text-[14px] font-semibold transition-colors disabled:opacity-50"
            >
              {submitting && <Icon icon="ph:spinner" className="w-4 h-4 animate-spin" />}
              Daftarkan Member
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddMemberModal;
